import { createFileRoute } from '@tanstack/react-router'
import { useState, type ReactNode } from 'react'
import ChevronDown from '~icons/lucide/chevron-down'
import Mail from '~icons/lucide/mail'
import Search from '~icons/lucide/search'

import { supportFaqSections } from '#/content/support/faqs.js'
import { ThemeToggle } from '#/ui/ThemeToggle.js'
import { TempoLogo } from '#/ui/TempoLogo.js'

type FaqAnswerBlock =
  | { text: string; type: 'paragraph' }
  | { items: readonly string[]; type: 'list' }
  | { code: string; type: 'code' }

type Faq = {
  answer: readonly FaqAnswerBlock[]
  id: string
  question: string
}

type FaqSection = {
  description: string
  id: string
  items: readonly Faq[]
  title: string
}

/** Route for Tempo's public support and FAQ page. */
export const Route = createFileRoute('/support/')({
  component: SupportPage,
  head: () => ({
    links: [{ href: 'https://wallet.tempo.xyz/support', rel: 'canonical' }],
    meta: [
      { title: 'Support | Tempo Wallet' },
      {
        content: 'Find answers to common Tempo Wallet questions and contact support.',
        name: 'description',
      },
    ],
  }),
})

function SupportPage() {
  const [query, setQuery] = useState('')
  const query_trimmed = query.trim().toLowerCase()
  const sections = getFilteredSections(query_trimmed)

  return (
    <main className="relative isolate min-h-dvh bg-background text-foreground">
      <div className="flex justify-center px-5 py-8 sm:px-8 lg:py-12">
        <article className="flex w-full max-w-4xl flex-col gap-8">
          <SupportHeader />

          <div className="flex flex-col gap-6 border-b border-border pb-8">
            <div className="flex flex-col gap-3">
              <p className="text-label-13 text-foreground-secondary">Tempo Wallet support</p>
              <h1 className="text-heading-40">Frequently Asked Questions</h1>
              <p className="max-w-2xl text-copy-14 text-foreground-secondary">
                Answers for passkeys, deposits, CLI usage, and MPP. If your question is not covered,
                contact{' '}
                <a
                  className="text-foreground underline underline-offset-3 transition-[opacity] duration-150 hover:opacity-70 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
                  href="mailto:support@tempo.xyz"
                >
                  support@tempo.xyz
                </a>
                .
              </p>
            </div>

            <label className="relative block">
              <span className="sr-only">Search support questions</span>
              <Search
                aria-hidden="true"
                className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-foreground-secondary"
              />
              <input
                aria-label="Search support questions"
                className="h-11 w-full rounded-body border border-border bg-surface py-2 pr-3 pl-10 text-copy-14 text-foreground outline-none transition-[background-color,border-color] placeholder:text-foreground-secondary hover:border-border-hover focus:border-border-focus focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search question here"
                type="search"
                value={query}
              />
            </label>

            <nav aria-label="FAQ sections" className="flex flex-wrap gap-2">
              {supportFaqSections.map((section) => (
                <a
                  className="rounded-button border border-border bg-surface px-3 py-2 text-label-13 text-foreground-secondary transition-[background-color,border-color,color] hover:border-border-hover hover:bg-surface-hover hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
                  href={`#${section.id}`}
                  key={section.id}
                >
                  {section.title}
                </a>
              ))}
            </nav>
          </div>

          {sections.length > 0 ? (
            <section aria-label="Support FAQs" className="flex flex-col gap-3">
              {sections.map((section, index) => (
                <SectionAccordion
                  index={index}
                  key={section.id}
                  query={query_trimmed}
                  section={section}
                />
              ))}
            </section>
          ) : (
            <div className="flex flex-col gap-3 rounded-body border border-border bg-surface p-5">
              <h2 className="text-heading-16">No matching questions</h2>
              <p className="text-copy-14 text-foreground-secondary">
                Try a different search term, or contact support and we will help you find the right
                answer.
              </p>
              <button
                className="w-fit rounded-button text-label-14 text-foreground underline decoration-border underline-offset-4 transition-[opacity,text-decoration-color] hover:opacity-75 hover:decoration-current focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
                onClick={() => setQuery('')}
                type="button"
              >
                Clear search
              </button>
            </div>
          )}

          <ContactSection />
        </article>
      </div>
    </main>
  )
}

function SupportHeader() {
  return (
    <header className="flex flex-col gap-5 border-b border-border pb-6 sm:flex-row sm:items-center sm:justify-between">
      <a
        aria-label="Go to Tempo Wallet"
        className="w-fit text-foreground transition-[opacity] duration-150 hover:opacity-70 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-focus"
        href="/"
      >
        <TempoLogo className="h-[18px] w-auto" />
      </a>
      <div className="flex flex-wrap items-center gap-4">
        <nav aria-label="Support" className="flex flex-wrap gap-4 text-label-13">
          <a
            className="text-foreground underline decoration-current underline-offset-4 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-focus"
            href="/support"
          >
            FAQ
          </a>
          <a
            className="text-foreground-secondary underline decoration-transparent underline-offset-4 transition-[color,text-decoration-color] duration-150 hover:text-foreground hover:decoration-current focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-focus"
            href="#contact"
          >
            Contact
          </a>
          <a
            className="text-foreground-secondary underline decoration-transparent underline-offset-4 transition-[color,text-decoration-color] duration-150 hover:text-foreground hover:decoration-current focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-focus"
            href="/support/terms-of-service"
          >
            Terms of Service
          </a>
          <a
            className="text-foreground-secondary underline decoration-transparent underline-offset-4 transition-[color,text-decoration-color] duration-150 hover:text-foreground hover:decoration-current focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-focus"
            href="/support/privacy-policy"
          >
            Privacy Policy
          </a>
        </nav>
        <ThemeToggle />
      </div>
    </header>
  )
}

function SectionAccordion(props: { index: number; query: string; section: FaqSection }) {
  const { index, query, section } = props
  const [open, setOpen] = useState(index === 0)
  const expanded = !!query || open
  const panelId = `${section.id}-panel`

  return (
    <section
      className="group rounded-body border border-border bg-surface"
      id={section.id}
    >
      <h2>
        <button
          aria-controls={panelId}
          aria-expanded={expanded}
          className="flex w-full cursor-pointer items-center gap-4 px-4 py-4 text-left outline-none focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-focus"
          onClick={() => {
            if (!query) setOpen((open) => !open)
          }}
          type="button"
        >
          <span className="flex min-w-0 flex-1 flex-col gap-1">
            <span className="text-heading-16 text-foreground">{section.title}</span>
            <span className="text-copy-14 text-foreground-secondary">{section.description}</span>
          </span>
          <ChevronDown
            aria-hidden="true"
            className="size-4 shrink-0 text-foreground-secondary transition-transform duration-150 data-expanded:rotate-180"
            data-expanded={expanded || undefined}
          />
        </button>
      </h2>

      {expanded ? (
        <div className="border-t border-border px-4" id={panelId}>
          {section.items.map((faq) => (
            <FaqAccordion faq={faq} key={faq.id} query={query} />
          ))}
        </div>
      ) : null}
    </section>
  )
}

function FaqAccordion(props: { faq: Faq; query: string }) {
  const { faq, query } = props
  const [open, setOpen] = useState(false)
  const expanded = !!query || open
  const panelId = `${faq.id}-panel`

  return (
    <div className="border-t border-border first:border-t-0" id={faq.id}>
      <h3>
        <button
          aria-controls={panelId}
          aria-expanded={expanded}
          className="flex w-full cursor-pointer items-start gap-3 py-4 text-left outline-none focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-focus"
          onClick={() => {
            if (!query) setOpen((open) => !open)
          }}
          type="button"
        >
          <span className="min-w-0 flex-1 text-label-14 text-foreground">{faq.question}</span>
          <ChevronDown
            aria-hidden="true"
            className="size-4 shrink-0 translate-y-0.5 text-foreground-secondary transition-transform duration-150 data-expanded:rotate-180"
            data-expanded={expanded || undefined}
          />
        </button>
      </h3>
      {expanded ? (
        <div
          className="flex flex-col gap-3 pb-5 text-copy-14 text-foreground-secondary sm:pr-10"
          id={panelId}
        >
          {faq.answer.map((block, index) => (
            <AnswerBlock block={block} key={index} />
          ))}
        </div>
      ) : null}
    </div>
  )
}

function AnswerBlock(props: { block: FaqAnswerBlock }) {
  const { block } = props
  if (block.type === 'list')
    return (
      <ul className="flex list-disc flex-col gap-2 pl-5">
        {block.items.map((item) => (
          <li key={item}>{renderInline(item)}</li>
        ))}
      </ul>
    )

  if (block.type === 'code')
    return (
      <pre className="overflow-x-auto rounded-body border border-border bg-pane p-3 text-label-13 text-foreground">
        <code>{block.code}</code>
      </pre>
    )

  return <p>{renderInline(block.text)}</p>
}

function ContactSection() {
  return (
    <section className="grid gap-4 border-t border-border pt-8 sm:grid-cols-2" id="contact">
      <ContactMethod
        href="mailto:support@tempo.xyz"
        label="General Support"
        value="support@tempo.xyz"
      />
      <ContactMethod
        href="mailto:lert@tempo.xyz"
        label="Law Enforcement Inquiries"
        value="lert@tempo.xyz"
      />
    </section>
  )
}

function ContactMethod(props: { href: string; label: string; value: string }) {
  const { href, label, value } = props
  return (
    <div className="flex flex-col gap-3 rounded-body border border-border bg-surface p-4">
      <div className="flex items-center gap-2">
        <Mail aria-hidden="true" className="size-4 text-foreground-secondary" />
        <h2 className="text-heading-16 font-normal">{label}</h2>
      </div>
      <a
        className="w-fit text-copy-14 text-foreground underline underline-offset-3 transition-[opacity] duration-150 hover:opacity-70 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
        href={href}
      >
        {value}
      </a>
    </div>
  )
}

function getFilteredSections(query: string): FaqSection[] {
  if (!query) return [...supportFaqSections]
  return supportFaqSections
    .map((section) => ({
      ...section,
      items: section.items.filter((faq) => getFaqText(faq).includes(query)),
    }))
    .filter((section) => section.items.length > 0)
}

function getFaqText(faq: Faq) {
  return [
    faq.question,
    ...faq.answer.flatMap((block) => {
      if (block.type === 'list') return block.items
      if (block.type === 'code') return block.code
      return block.text
    }),
  ]
    .join(' ')
    .toLowerCase()
}

function renderInline(text: string) {
  const nodes: ReactNode[] = []
  const pattern = /https?:\/\/[^\s]+/g
  let index = 0

  for (const match of text.matchAll(pattern)) {
    if (match.index > index) nodes.push(text.slice(index, match.index))
    const href = match[0]
    nodes.push(
      <a
        className="text-foreground underline underline-offset-3 transition-[opacity] duration-150 hover:opacity-70 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
        href={href}
        key={nodes.length}
        rel="noopener noreferrer"
        target="_blank"
      >
        {href}
      </a>,
    )
    index = match.index + href.length
  }

  if (index < text.length) nodes.push(text.slice(index))
  return nodes
}
